import {
  ControlOutlined,
  FileTextOutlined,
  LinkOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Empty, Form, Input, Progress, Spin, Upload, message } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { addDoc, collection, onSnapshot, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider/AuthProvider";
import { db } from "../firebase";

type LibraryDoc = {
  id: string;
  title?: string;
  name?: string;
  url?: string;
  docUrl?: string;
  fileUrl?: string;
  path?: string;
  description?: string;
  category?: string;
  createdAt?: unknown;
};

const getDocUrl = (doc: LibraryDoc) =>
  doc.url ||
  doc.docUrl ||
  doc.fileUrl ||
  doc.path ||
  (doc as any).link ||
  (doc as any).href ||
  (doc as any).storagePath ||
  (doc as any).pdfUrl ||
  (doc as any).pdfPath ||
  "";

const getDocTitle = (doc: LibraryDoc) =>
  doc.title || doc.name || doc.id || "Untitled Document";

export const Library = () => {
  const [docs, setDocs] = useState<Array<LibraryDoc>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadForm] = Form.useForm();
  const { currentRole } = useAuth();
  const nav = useNavigate();
  const isAdmin = currentRole?.admin === true;

  useEffect(() => {
    setLoading(true);

    // Use a plain collection listener. orderBy("createdAt") excludes documents
    // that do not have createdAt, which was preventing older PDF records from
    // appearing in the library.
    const unsub = onSnapshot(
      collection(db, "docs"),
      (snap) => {
        const loadedDocs = snap.docs
          .map((snapDoc) => ({
            id: snapDoc.id,
            ...(snapDoc.data() as Omit<LibraryDoc, "id">),
          }))
          .sort((a: any, b: any) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
            return bTime - aTime;
          });

        setDocs(loadedDocs);
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load docs:", err);
        setLoadError("Unable to load library documents.");
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  const openDoc = (doc: LibraryDoc) => {
    const url = getDocUrl(doc);

    nav("/library/doc", {
      state: {
        url,
        title: getDocTitle(doc),
        doc,
      },
    });
  };

  const sanitizeFileName = (fileName: string) =>
    fileName
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9._-]/g, "_")
      .replace(/_+/g, "_");

  const uploadDocument = async () => {
    try {
      if (!uploadFile) {
        message.error("Please select a document to upload.");
        return;
      }

      const values = await uploadForm.validateFields();
      setUploading(true);
      setUploadProgress(0);

      const safeName = sanitizeFileName(uploadFile.name);
      const storagePath = `docs/${Date.now()}_${safeName}`;
      const storageRef = ref(getStorage(), storagePath);
      const uploadTask = uploadBytesResumable(storageRef, uploadFile, {
        contentType: uploadFile.type || "application/octet-stream",
        customMetadata: {
          originalName: uploadFile.name,
        },
      });

      const uploadResult = await new Promise<{ path: string; url: string }>(
        (resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              setUploadProgress(
                Math.round(
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
                ),
              );
            },
            reject,
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({ path: uploadTask.snapshot.ref.fullPath, url });
            },
          );
        },
      );

      await addDoc(collection(db, "docs"), {
        title: values.title,
        description: values.description || "",
        category: values.category || "Document",
        url: uploadResult.url,
        path: uploadResult.path,
        fileName: uploadFile.name,
        contentType: uploadFile.type || null,
        createdAt: serverTimestamp(),
      });

      message.success("Document uploaded to library.");
      uploadForm.resetFields();
      setUploadFile(null);
      setUploadProgress(0);
    } catch (err) {
      console.error("Document upload failed:", err);
      message.error("Document upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#1f1e23] text-white selection:bg-[#F6B34B] selection:text-[#1D1928]">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,_rgba(246,179,75,0.08),_transparent_35%),linear-gradient(to_bottom,_rgba(0,0,0,0.15),_transparent)]" />

      <main className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-8 px-6 py-10 md:py-16">
        <header className="border border-white/10 bg-[#1D1928]/50 p-6 backdrop-blur-sm md:p-10">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C6B73] animate-pulse">
                {"// LIBRARY.DOCS :: KNOWLEDGE_BASE"}
              </p>
              <h1 className="mt-5 font-oswald text-5xl uppercase leading-none tracking-tighter text-[#F6B34B] md:text-7xl">
                SWS Library
              </h1>
              <p className="mt-5 max-w-xl border-l-2 border-[#F6B34B] pl-4 font-mono text-sm leading-relaxed text-gray-300 md:text-base">
                Internal documents, references, and production resources.
                <span className="mt-2 block text-[#5C6B73]">
                  {loading ? "Loading documents..." : `${docs.length} documents available`}
                </span>
              </p>
            </div>

            <button
              className="border border-[#5C6B73] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#5C6B73] transition-all hover:border-[#F6B34B] hover:text-[#F6B34B]"
              onClick={() => nav("/tools")}
              type="button"
            >
              <ControlOutlined /> Tools
            </button>
          </div>
        </header>

        {isAdmin && (
          <section className="border border-[#9A6D26]/20 bg-black/10">
            <div className="border-b border-white/10 px-5 py-4 md:px-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
                {"// docs.upload"}
              </p>
              <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
                Upload Document
              </h2>
              <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
            </div>

            <div className="p-5 md:p-8">
              <Form form={uploadForm} layout="vertical" className="max-w-2xl">
                <Form.Item
                  name="title"
                  label={<span className="font-mono uppercase text-[#5C6B73]">Title</span>}
                  rules={[{ required: true, message: "Document title is required" }]}
                >
                  <Input placeholder="Document title" />
                </Form.Item>

                <Form.Item
                  name="category"
                  label={<span className="font-mono uppercase text-[#5C6B73]">Category</span>}
                >
                  <Input placeholder="Document, Contract, Policy..." />
                </Form.Item>

                <Form.Item
                  name="description"
                  label={<span className="font-mono uppercase text-[#5C6B73]">Description</span>}
                >
                  <Input.TextArea rows={3} placeholder="Short description" />
                </Form.Item>

                <div className="mb-4 flex flex-col gap-3">
                  <Upload
                    accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,image/*"
                    beforeUpload={(file) => {
                      setUploadFile(file);
                      if (!uploadForm.getFieldValue("title")) {
                        uploadForm.setFieldValue(
                          "title",
                          file.name.replace(/\.[^.]+$/, ""),
                        );
                      }
                      return false;
                    }}
                    maxCount={1}
                    fileList={
                      uploadFile
                        ? [
                            {
                              uid: "library-doc",
                              name: uploadFile.name,
                              status: "done",
                            } as UploadFile,
                          ]
                        : []
                    }
                    onRemove={() => {
                      setUploadFile(null);
                      setUploadProgress(0);
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Select Document</Button>
                  </Upload>

                  {uploading && <Progress percent={uploadProgress} />}
                </div>

                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={uploading}
                  onClick={uploadDocument}
                >
                  Upload to Library
                </Button>
              </Form>
            </div>
          </section>
        )}

        <section className="border border-[#9A6D26]/20 bg-black/10">
          <div className="border-b border-white/10 px-5 py-4 md:px-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              {"// docs.index"}
            </p>
            <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
              Documents
            </h2>
            <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
          </div>

          <div className="p-5 md:p-8">
            {loading && (
              <div className="flex min-h-[260px] items-center justify-center">
                <Spin size="large" />
              </div>
            )}

            {!loading && loadError && (
              <div className="border border-red-400/30 bg-red-500/10 p-6 font-mono text-sm uppercase tracking-widest text-red-200">
                {loadError}
              </div>
            )}

            {!loading && !loadError && docs.length === 0 && (
              <div className="bg-white p-8">
                <Empty description="No documents found" />
              </div>
            )}

            {!loading && !loadError && docs.length > 0 && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {docs.map((singleDoc) => {
                  const url = getDocUrl(singleDoc);
                  const disabled = !url;

                  return (
                    <button
                      key={singleDoc.id}
                      className="group min-h-[150px] border border-white/10 bg-white/[0.02] p-5 text-left transition-all duration-300 hover:border-[#F6B34B]/70 hover:bg-white/[0.05] disabled:cursor-not-allowed disabled:opacity-40"
                      onClick={() => openDoc(singleDoc)}
                      disabled={disabled}
                      type="button"
                    >
                      <div className="mb-8 flex items-center justify-between text-[#F6B34B]">
                        <FileTextOutlined className="text-xl transition-transform duration-500 group-hover:scale-110" />
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#5C6B73] group-hover:text-[#F6B34B]">
                          {disabled ? "[ Missing URL ]" : "[ Open ]"}
                        </span>
                      </div>
                      <h3 className="font-oswald text-xl uppercase tracking-tight text-white transition-colors group-hover:text-[#F6B34B]">
                        {getDocTitle(singleDoc)}
                      </h3>
                      {singleDoc.description && (
                        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-gray-400">
                          {singleDoc.description}
                        </p>
                      )}
                      <div className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#5C6B73]">
                        <LinkOutlined />
                        <span>{singleDoc.category || "Document"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};
